/* Custom Input Component to be used with Formik */
import React, { useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text, FormErrorMessage, Image, Icon, Clamp,
} from 'components/atoms';

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FieldImage = ({
  title,
  field, // { name, value, onChange, onBlur }
  form: { touched, errors }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  onClick,
}) => {
  const error = useMemo(() => {
    const errorExists = touched[field.name] && errors[field.name];
    if (errorExists) return errors[field.name].url || errors[field.name].alt;
    return false;
  }, [touched, errors, field.name]);

  const [isHover, setHover] = useState(false);

  const { alt, url } = field.value;
  const computedIsLocal = useMemo(
    () => {
      if (url && url.indexOf('http') === -1) return true
      return false
    },
    [url]
  );

  const hasImage = useMemo(() => url && url !== '', [url]);
  const hasAlt = useMemo(() => alt && alt !== '', [alt]);
  const computedText = useMemo(() => {
    if (!hasImage) return 'Selecionar imagem';
    if (!hasAlt) return 'Sem descrição';
    return alt;
  }, [hasAlt, alt, hasImage]);

  const handleClick = () => {
    onClick();
  };

  return (
    <div>
      {title && <Text modifiers={['base']}>{title}</Text>}
      <ImageInput
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
      >
        <ImageWrapper hasImage={hasImage} isHover={isHover}>
          {isHover && hasImage && (
            <ImageHover>
              <Icon height={16} color="#FFF" icon="edit" />
            </ImageHover>
          )}
          {hasImage ? (
            <Image src={url} alt={alt} isPortal={computedIsLocal} height={36} />
          ) : (
            <Icon icon="add_photo" color="#FFF" height={20} />
          )}
        </ImageWrapper>
        {hasImage ? (
          <Clamp>
            <Text>{computedText}</Text>
          </Clamp>
        ) : (
          <Text modifiers={['lighter']}>Selecionar imagem</Text>
        )}
      </ImageInput>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      <InputSpacingFix />
    </div>
  );
};

const ImageInput = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: ${props => props.theme.values.borderSize} solid
    ${props => props.theme.colors.base};
  padding: ${props => `${props.theme.spacing.xs2} 0`};
  align-items: flex-start;
  cursor: pointer;
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 36px;
  width: calc(36px * 16 / 9);
  background-color: ${({ hasImage, isHover, theme }) => {
    if (hasImage) return theme.colors.bg;
    if (isHover && !hasImage) return theme.colors.primary;
    return theme.colors.bgLighterS;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${props => props.theme.spacing.xs2};
`;

const ImageHover = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.bgHover};
`;

FieldImage.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.shape({
      title: PropTypes.string,
      alt: PropTypes.string,
      url: PropTypes.string,
      isLocal: PropTypes.bool,
    }),
  }),
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
  }).isRequired,
};

FieldImage.defaultProps = {
  onClick: () => null,
  field: {
    value: {
      title: '',
      alt: '',
      url: '',
      isLocal: true,
    },
  },
};

export default FieldImage;
